/*----------------------------------
- COMPONENT: GLOSSARY SECTION
----------------------------------*/

/**
 * @docs docs/features/glossary
 *
 * Scopes the glossary's "first use" to one section of a page, for `MarketingGlossaryText` (the marketing
 * `GlossaryTerm` cards). The old `.ud-term` renderer (`GlossaryText`, `GlossaryTerm`, `edgeFlip.ts`) left in the batch 6
 * sweep: every page that explains its words had moved to the marketing renderer.
 *
 * @rule First use per section, never per page. A section owns its `seen` set; without a provider every string is its
 *       own section, which is the right default for a one-paragraph surface such as an FAQ answer.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { ComponentChildren } from 'preact';
import { createContext } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

/*----------------------------------
- SECTION SCOPE
----------------------------------*/

const GlossarySectionContext = createContext<Set<string> | null>(null);

/**
 * The set lives as long as the provider and is filled as the subtree renders, in document order, which is the order a
 * reader meets the words in. Each renderer remembers which terms it claimed, so rendering it again gives the same
 * cards instead of finding them all taken.
 */
export const GlossarySection = ({ children }: { children: ComponentChildren }) => {
    const seen = useMemo(() => new Set<string>(), []);
    return <GlossarySectionContext.Provider value={seen}>{children}</GlossarySectionContext.Provider>;
};

/** The terms the enclosing section already explained, or null outside a `GlossarySection`. */
export const useGlossarySectionSeen = (): Set<string> | null => useContext(GlossarySectionContext);
